import { type IPaymentQueryParams } from "@/lib/centra-client/interfaces/IPaymentService"
import { type CheckoutAPISelectionSelection } from "@/types/centra/checkout-api/selection/get-selection"

type StoredAddress =
    | Partial<CheckoutAPISelectionSelection["address"]>
    | Partial<CheckoutAPISelectionSelection["shippingAddress"]>
    | null
    | undefined

type InitAddress = NonNullable<IPaymentQueryParams["address"]>

const ADDRESS_FIELDS = [
    "firstName",
    "lastName",
    "email",
    "phoneNumber",
    "address1",
    "address2",
    "zipCode",
    "city",
    "state",
    "country"
] as const

/**
 * The address Centra already holds for the selection, reduced to its non-empty fields, in
 * the shape `POST /payment` accepts.
 *
 * With `paymentInitiateOnly: true` Centra treats a missing address as the new address and
 * blanks what it had stored, email included. Every email-conditioned voucher is then parked
 * until the email is written back. Handing Centra its own current values keeps the stored
 * address intact, and cannot re-anchor the country, because the country sent is the one the
 * selection already has.
 */
export const storedAddressForPaymentInit = (
    address: StoredAddress
): InitAddress | undefined => {
    if (!address) return undefined

    const picked: InitAddress = {}

    for (const field of ADDRESS_FIELDS) {
        const value = address[field]
        if (typeof value === "string" && value.trim() !== "") {
            picked[field] = value
        }
    }

    return Object.keys(picked).length > 0 ? picked : undefined
}
